import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { applyTheme, getInitialTheme } from "./lib/theme.js";
import { registerServiceWorker } from "./lib/sw-register.js";
import { installGlobalErrorReporter } from "./lib/report-error.js";
import { flushOutbox } from "./lib/api.js";
import { claimChunkReload } from "./lib/chunk-reload.js";
import "./styles/global.css";
import { initFlagsFromUrl } from "./lib/flags.js";

// Apply theme BEFORE React renders, to avoid a flash of wrong theme.
applyTheme(getInitialTheme());

// Register the service worker. Production-only; localhost dev would
// otherwise cache stale Vite HMR chunks.
registerServiceWorker();

// Report uncaught errors + unhandled rejections so post-login / onboarding
// failures are visible in /app/admin instead of dying in the user's console.
installGlobalErrorReporter();

// Offline attempt outbox (E7): drain queued drill attempts on load and whenever
// we regain connectivity or the tab refocuses. Registered once at module load
// (not in a component effect) so StrictMode's double-invoke can't double-bind.
window.addEventListener("online", () => void flushOutbox());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void flushOutbox();
});
void flushOutbox();

// With code-split routes, a lazy chunk can 404 after a deploy (the old hash is
// gone and the SW's skipWaiting briefly mixes old shell + new chunks). Vite
// fires `vite:preloadError`; reload once per BUILD to fetch the fresh chunk
// graph. The old guard here cleared itself on a 10-second timer, which is a
// race: past ten seconds a genuinely broken chunk could reload forever. Keying
// on the build makes a loop impossible while still self-healing on every deploy.
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  if (claimChunkReload("preload")) window.location.reload();
});

// Read ?ui= before the first render so the flag is already settled and the
// screen does not swap after hydration.
initFlagsFromUrl();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary label="root">
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
